import { graphql, useStaticQuery } from "gatsby"
import React, { useCallback, useEffect, useState } from "react"
import styled, { ThemeProvider } from "styled-components"
import { Normalize } from "styled-normalize"
import { useSmoothScroll } from "../helpers/smooth-scroll"
import Footer from "../navigation/containers/footer/footer.container"
import theme from "../theme"
import { Modal, ModalType, PromoBar } from "../ui/"
import BackToTop from "./common/back-to-top.component"
import {
  commerceGridSpacing,
  gridSpacing,
} from "./common/general-styles.component"
import Banner, { BannerProps } from "./common/layout/banner.component"
import Header from "../navigation/containers/header/header.container"
import SkipLinksNavigation, {
  contentStartId,
} from "./common/skip-links-navigation.component"
import GlobalStyle from "./global-style.component"
import TranslateContentWrapper from "../helpers/translate-content-wrapper"
import { AppFunctionComponent, GatsbyImage, TextBuilder } from "../types"
import { SubmenuItem } from "../navigation/types/menu.interface"
import Cookies from "js-cookie"
import useTranslationHook from "../hooks/use-translation.hook"
import { CustomButtonProps } from "../ui/components/modal/popup/popup.component"
import { linkIncludePaths } from "../helpers/linkIncludePath"
import { useMouseHook } from "../hooks/use-mouse.hook"

interface Props {
  masterPage?: boolean
  withSmoothScroll?: boolean
  withBackToTop?: boolean
  commerceCheckout?: boolean
  landing?: boolean
  isSleepBetter?: boolean
}

export const Main = styled.main<Props>`
  --grid-spacing: "50px";
  display: grid;
  grid-auto-rows: auto;
  grid-template-columns: 100%;
  ${({ masterPage }) => !masterPage && gridSpacing};
  ${({ commerceCheckout }) => commerceCheckout && commerceGridSpacing};
  grid-row-gap: var(--grid-spacing);

  @media (min-width: 960px) {
    padding-top: 0;
    margin-top: ${({ landing }) => (landing ? "0" : "15px")};
  }
`

interface LayoutProps extends Props {
  topNavigationItems: SubmenuItem[]
  footerNavigationItems: SubmenuItem[]
  promobarContent: TextBuilder
  darkMode?: boolean
  withoutFooter?: boolean
  smallHeader?: boolean
}

interface PopupProps {
  buttonLink: string
  enabled: boolean
  buttonText: string
  backgroundImage: GatsbyImage
  mobileBackgroundImage: GatsbyImage
  node_locale: string
  secondButtonLink?: string
  includedPaths?: string[]
  excludedBlogTags?: string
  typeOfPopup: string
  style: {
    window: {
      web: {
        height: string
        width: string
      }
      mobile: {
        height: string
        width: string
      }
    }
    button: {
      web: CustomButtonProps
      mobile: CustomButtonProps
    }
    closeButton: {
      web: CustomButtonProps
      mobile: CustomButtonProps
    }
  }
}

interface DataQuery {
  site: {
    siteMetadata: {
      title: string
    }
  }
  popup: {
    nodes: PopupProps[]
  }
  banner?: BannerProps
}

const Layout: AppFunctionComponent<LayoutProps> = ({
  children,
  masterPage,
  withBackToTop,
  withSmoothScroll,
  commerceCheckout,
  topNavigationItems,
  footerNavigationItems,
  promobarContent,
  landing,
  darkMode,
  withoutFooter,
  isSleepBetter,
  smallHeader,
}) => {
  const [active, setActive] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [popupAppeared, setPopupAppeared] = useState(false)
  const [currentPopup, setCurrentPopup] = useState<PopupProps | null>(null)
  const { isMouseInHeader } = useMouseHook()

  const { site, banner, popup } = useStaticQuery<DataQuery>(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
      banner: contentfulSection(contentfulid: { eq: "breath-banner" }) {
        text {
          raw
        }
      }
      popup: allContentfulPopupWindow(filter: { enabled: { eq: true } }) {
        nodes {
          buttonLink
          buttonText
          enabled
          backgroundImage {
            gatsbyImageData(quality: 100)
          }
          mobileBackgroundImage {
            gatsbyImageData(quality: 100)
          }
          node_locale
          secondButtonLink
          includedPaths
          excludedBlogTags
          typeOfPopup
          style {
            window {
              web {
                width
                height
              }
              mobile {
                width
                height
              }
            }
            button {
              mobile {
                width
                height
                top
                fontSize
                left
              }
              web {
                backgroundColor
                height
                width
                bottom
                left
                borderRadius
                color
                fontFamily
                fontSize
                fontWeight
                textTransform
                lineHeight
              }
            }
            closeButton {
              mobile {
                width
                height
                top
                left
              }
              web {
                backgroundColor
                height
                width
                top
                left
              }
            }
          }
        }
      }
    }
  `)
  const { language } = useTranslationHook()

  const activePopups = popup.nodes.filter((el) =>
    el.node_locale.includes(language)
  )

  if (withSmoothScroll) {
    useSmoothScroll()
  }

  const setActiveModal = useCallback(() => {
    for (let index = 0; index < activePopups.length; index++) {
      if (activePopups[index].enabled) {
        const isModalShown = Cookies.get(`popup-${index}`)
        let includedPaths = activePopups[index].includedPaths
        if (language === "pl") {
          includedPaths = activePopups[index].includedPaths?.map(
            (path) => `/pl${path}`
          )
        }
        if (linkIncludePaths(window.location.pathname, includedPaths)) {
          if (isModalShown) {
            setActive(false)
            setCurrentPopup(null)
          } else if (!isModalShown && !active) {
            Cookies.set(`popup-${index}`, "true", { expires: 1 })
            setActive(true)
            setCurrentPopup(activePopups[index])
            setPopupAppeared(true)
            break
          }
        }
      }
    }
  }, [activePopups, language, active])

  const desktopImage =
    currentPopup?.backgroundImage.gatsbyImageData.images.fallback?.src
      .replace("q=100", "q=90")
      .replace("fm=png", "fm=webp")
  const mobileImage =
    currentPopup?.mobileBackgroundImage.gatsbyImageData.images.fallback?.src
      .replace("q=100", "q=90")
      .replace("fm=png", "fm=webp")
  const buttonLink = currentPopup?.buttonLink
  const secondButtonLink = currentPopup?.secondButtonLink
  const buttonText = currentPopup?.buttonText
  const buttonMobileStyle = currentPopup?.style.button.mobile
  const buttonWebStyle = currentPopup?.style.button.web
  const closeButtonMobileStyle = currentPopup?.style.closeButton.mobile
  const closeButtonWebStyle = currentPopup?.style.closeButton.web
  const windowWebStyle = currentPopup?.style.window.web
  const windowMobileStyle = currentPopup?.style.window.mobile

  useEffect(() => {
    const isPurePage =
      window.location.pathname.includes("/mudita-pure") ||
      window.location.pathname.includes("/sar-checker")

    if (isPurePage) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!popupAppeared) {
          setActiveModal()
          event.returnValue = "Are you sure you want to leave?"
        }
      }

      const handleMouseLeave = (event: MouseEvent) => {
        if (event.clientY <= 0 && !popupAppeared) {
          setActiveModal()
        }
      }

      const handleVisibilityChange = () => {
        if (document.hidden && !popupAppeared) {
          setActiveModal()
        }
      }

      const handleBackButton = () => {
        if (!popupAppeared) {
          setActiveModal()
          window.history.pushState(null, "", window.location.href) // Prevent back navigation
        }
      }

      const startInactivityTimer = () => {
        const inactivityTimeout = setTimeout(() => {
          if (!popupAppeared) {
            setActiveModal()
          }
        }, 30000) // 30 seconds of inactivity
        return inactivityTimeout
      }

      // Set event listeners
      window.addEventListener("beforeunload", handleBeforeUnload)
      document.addEventListener("mouseleave", handleMouseLeave)
      document.addEventListener("visibilitychange", handleVisibilityChange)

      // Handle back button press for mobile
      window.history.pushState(null, "", window.location.href)
      window.addEventListener("popstate", handleBackButton)

      // Start inactivity timer
      let inactivityTimer = startInactivityTimer()
      const resetInactivityTimer = () => {
        clearTimeout(inactivityTimer)
        inactivityTimer = startInactivityTimer()
      }

      document.addEventListener("touchstart", resetInactivityTimer)
      document.addEventListener("scroll", resetInactivityTimer)

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload)
        document.removeEventListener("mouseleave", handleMouseLeave)
        document.removeEventListener("visibilitychange", handleVisibilityChange)
        window.removeEventListener("popstate", handleBackButton)
        document.removeEventListener("touchstart", resetInactivityTimer)
        document.removeEventListener("scroll", resetInactivityTimer)
        clearTimeout(inactivityTimer)
      }
    } else {
      const handleScroll = () => {
        // Check if the user has scrolled, for example, 100 pixels
        if (window.scrollY > 70) {
          setScrolled(true)
        }
      }

      // Attach the event listener to the window
      window.addEventListener("scroll", handleScroll)

      if (scrolled && !isMouseInHeader && !popupAppeared) {
        const timer = setTimeout(() => {
          setActiveModal()
        }, 20000)
        return () => clearInterval(timer)
      }

      // Cleanup the event listener on component unmount
      return () => {
        window.removeEventListener("scroll", handleScroll)
      }
    }
  }, [scrolled, popupAppeared, isMouseInHeader, setActiveModal])

  if (
    typeof window !== "undefined" &&
    !window?.location?.href.includes("/community/blog")
  ) {
    return (
      <ThemeProvider theme={theme}>
        <TranslateContentWrapper>
          <Normalize />
          <GlobalStyle $isDark={darkMode} />
          <SkipLinksNavigation />
          {banner && <Banner text={banner.text} />}
          {!landing && !isSleepBetter && <PromoBar content={promobarContent} />}
          {currentPopup && (
            <Modal
              onClose={() => setActive(false)}
              active={active}
              modalType={ModalType.CustomPopup}
              popupProps={{
                desktopImage,
                mobileImage,
                buttonLink,
                buttonText,
                buttonMobileStyle,
                buttonWebStyle,
                closeButtonMobileStyle,
                closeButtonWebStyle,
                windowMobileStyle,
                windowWebStyle,
                secondButtonLink,
              }}
            />
          )}
          <Header
            navigationItems={topNavigationItems}
            siteTitle={site.siteMetadata.title}
            isDark={darkMode}
            smallHeader={smallHeader}
          />
          <Main
            masterPage={masterPage}
            commerceCheckout={commerceCheckout}
            id={contentStartId}
            landing={landing || smallHeader}
          >
            {children}
          </Main>
          {!withoutFooter && <Footer navigationItems={footerNavigationItems} />}
          {withBackToTop && <BackToTop />}
        </TranslateContentWrapper>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Normalize />
      <GlobalStyle $isDark={darkMode} />
      <SkipLinksNavigation />
      {banner && <Banner text={banner.text} />}
      {(!landing || !isSleepBetter) && <PromoBar content={promobarContent} />}
      {currentPopup && (
        <Modal
          onClose={() => setActive(false)}
          active={active}
          modalType={ModalType.CustomPopup}
          popupProps={{
            desktopImage,
            mobileImage,
            buttonLink,
            buttonText,
            buttonMobileStyle,
            buttonWebStyle,
            closeButtonMobileStyle,
            closeButtonWebStyle,
            windowMobileStyle,
            windowWebStyle,
            secondButtonLink,
          }}
        />
      )}
      <Header
        navigationItems={topNavigationItems}
        siteTitle={site.siteMetadata.title}
        isDark={darkMode}
        smallHeader={smallHeader}
      />
      <Main
        masterPage={masterPage}
        commerceCheckout={commerceCheckout}
        id={contentStartId}
        landing={landing || smallHeader}
      >
        {children}
      </Main>
      {!withoutFooter && <Footer navigationItems={footerNavigationItems} />}
      {withBackToTop && <BackToTop />}
    </ThemeProvider>
  )
}

export default Layout
